/**
 * Game-Night Hub (design card 1c) — one page per live event that replaces
 * the scattered Score/Host/Results/Edit links. It's the landing page when
 * you click a game night in the list, and where "Launch game night" from
 * the quiz builder drops you.
 *
 * Route: /events/:id/hub. Reads the full live event (quiz → rounds →
 * questions, venue defaults, teams, resolved welcome) from GET
 * /api/events/:id. Preflight is derived client-side from that data — no
 * new endpoint. Overrides/teams reuse the existing PUT/POST routes.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { estimateRuntime, missingChoiceKeys, missingChoiceLabel, quizContentCounts } from '../../utils/preflight';
import ScoreboardCard, { type ScoreboardSettings } from './ScoreboardCard';
import type { CustomSlide } from '../../utils/slideSequence';
import { customSlideLabel } from '../../utils/customSlides';
import NightSlidesCard from '../Slides/NightSlidesCard';
import { useRoomSignals } from '../../utils/useRealtime';
import CheckInPanel from '../CheckIn/CheckInPanel';
import CheckInQr from '../CheckIn/CheckInQr';
import { SegmentedControl, Switch } from '@radix-ui/themes';
import './GameNightHub.css';

type RoundType = 'STANDARD' | 'VISUAL' | 'AUDIO';
type QuestionType = 'TEXT' | 'SONG' | 'IMAGE';

interface Question {
  id: string;
  type: QuestionType;
  answer: string;
  audioPath: string | null;
  imagePath: string | null;
  orderIndex?: number;
  text?: string;
  // Multiple choice: the choices and the index of the correct one.
  choices?: string[];
  correctChoice?: number | null;
}

interface Round {
  id: string;
  orderIndex: number;
  title: string;
  roundType: RoundType;
  questions: Question[];
}

interface Team {
  id: string;
  name: string;
  playerCount: number;
  leftEarly: boolean;
  canonicalTeamId: number | null;
  checkedInAt: string | null;
}

interface HubEvent {
  id: string;
  name: string | null;
  date: string;
  pin: string;
  adminPin: string;
  status: 'LIVE' | 'COMPLETE';
  checkInEnabled: boolean;
  answerMode: 'PAPER' | 'PHONE' | 'MIXED';
  roundCloseMode: 'ON_REVEAL' | 'MANUAL';
  prizesOverride: string | null;
  rulesTextOverride: string | null;
  startTimeOverride: string | null;
  venue: {
    name: string;
    defaultPrizes: string | null;
    defaultRulesText: string | null;
    defaultStartTime: string | null;
  };
  quiz: {
    id: string;
    name: string;
    welcomePrizes: string | null;
    welcomeStartTime: string | null;
    rulesText: string | null;
    showDrawingSlide: boolean;
    drawingAfterRound: number | null;
    rounds: Round[];
  };
  teams: Team[];
  welcome: { prizes: string | null; rulesText: string | null; startTime: string | null };
  scoreboardEnabled: boolean;
  scoreboardTopN: number | null;
  // Plan features from the server (utils/entitlements.js).
  features?: { tvScoreboard?: boolean };
  // Custom slides placed into this night, in play order.
  customSlides?: CustomSlide[];
}

// A single preflight check derived from quiz content.
interface Check {
  key: string;
  label: string;
  ok: boolean;
  detail?: string; // e.g. "6/8"
  // Where "Fix" jumps — into the builder for content gaps.
  fixTo?: string;
  // One link per affected item, for example each question to fix.
  items?: { label: string; to: string; title?: string }[];
}

function computeChecks(ev: HubEvent): Check[] {
  const rounds = ev.quiz.rounds;
  const c = quizContentCounts(rounds);

  const rulesSet = Boolean((ev.welcome.rulesText || '').trim());
  const prizesSet = Boolean((ev.welcome.prizes || '').trim());

  const drawingPlaced =
    !ev.quiz.showDrawingSlide ||
    ev.quiz.drawingAfterRound == null ||
    (ev.quiz.drawingAfterRound >= 1 && ev.quiz.drawingAfterRound <= rounds.length);

  const editQuiz = `/quizzes/${ev.quiz.id}/edit`;

  const checks: Check[] = [];
  // Audio MP3s and visual images are OPTIONAL (hosts can play audio
  // off-platform; visual rounds are read aloud and we don't display images),
  // so they're not preflight blockers. Per-question upload status still shows
  // in the quiz editor for hosts who do use them.
  checks.push({
    key: 'answers',
    label: 'Every question has an answer',
    ok: c.answersMissing === 0 && c.totalQuestions > 0,
    detail: `${c.answered}/${c.totalQuestions}`,
    fixTo: editQuiz,
  });
  // A multiple choice question with no correct choice marks every phone
  // answer wrong. List each one with a link that opens it in the editor.
  const noKey = missingChoiceKeys(rounds);
  const hasChoices = rounds.some((r) => r.questions.some((q) => (q.choices ?? []).length >= 2));
  if (hasChoices) {
    checks.push({
      key: 'choicekeys',
      label: 'Every multiple choice question has a correct choice',
      ok: noKey.length === 0,
      items: noKey.map((m) => ({
        label: missingChoiceLabel(m),
        to: `${editQuiz}?question=${encodeURIComponent(m.questionId)}`,
        title: m.text,
      })),
    });
  }
  checks.push({
    key: 'rulesprizes',
    label: 'Rules & prizes set',
    ok: rulesSet && prizesSet,
    detail: rulesSet && prizesSet ? undefined : !rulesSet && !prizesSet ? 'both missing' : !rulesSet ? 'rules missing' : 'prizes missing',
  });
  checks.push({
    key: 'drawing',
    label:
      ev.quiz.showDrawingSlide
        ? `Drawing slide placed${ev.quiz.drawingAfterRound ? ` — after R${ev.quiz.drawingAfterRound}` : ' — after last round'}`
        : 'Drawing slide off (not used)',
    ok: drawingPlaced,
  });
  // A placed slide whose round no longer exists still plays, at the end.
  // Flag it so the host can move it before the night starts.
  const custom = ev.customSlides ?? [];
  if (custom.length > 0) {
    const misplaced = custom.filter((c) => c.fellBack);
    checks.push({
      key: 'customslides',
      label: 'Custom slides sit where you placed them',
      ok: misplaced.length === 0,
      detail: misplaced.length ? `${misplaced.map((c) => customSlideLabel(c)).join(', ')} moved to the end` : undefined,
    });
  }
  return checks;
}

// Which source feeds a resolved welcome field, for the overrides card.
// Mirrors resolveWelcome() on the server: this night's override wins, then
// the venue default, then whatever was typed on the night itself. Must check
// all three — reporting "quiz default" for a field nobody has filled in
// anywhere is how six venues ended up with empty defaults unnoticed.
function overrideSource(
  override: string | null,
  venueDefault: string | null,
  own: string | null,
): 'OVERRIDE' | 'venue default' | 'typed on this night' | 'not set' {
  if (override && override.trim()) {return 'OVERRIDE';}
  if (venueDefault && venueDefault.trim()) {return 'venue default';}
  if (own && own.trim()) {return 'typed on this night';}
  return 'not set';
}

export default function GameNightHub() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [ev, setEv] = useState<HubEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [addingTeam, setAddingTeam] = useState(false);
  const [marking, setMarking] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const res = await api.get(`/api/events/${id}`);
      setEv(res.data.data.event);
    } catch {
      setError('Failed to load game night');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // Phone check-ins appear live. The signal carries no data, so reload.
  useRoomSignals(id ? { event: 'join-host', eventId: id } : null, { 'teams-changed': () => load() }, load);

  const [savingMode, setSavingMode] = useState(false);
  const setAnswerMode = async (mode: HubEvent['answerMode']) => {
    if (!ev) return;
    const prev = ev.answerMode;
    setSavingMode(true);
    setEv({ ...ev, answerMode: mode });
    try {
      // Phone answers need phone check-in, so turn it on with them.
      const body: Record<string, unknown> = { answerMode: mode };
      if (mode !== 'PAPER' && !ev.checkInEnabled) body.checkInEnabled = true;
      await api.put(`/api/events/${ev.id}`, body);
      if (body.checkInEnabled) setEv((cur) => (cur ? { ...cur, checkInEnabled: true } : cur));
    } catch {
      setEv({ ...ev, answerMode: prev });
      window.alert('Could not change the answer mode');
    } finally {
      setSavingMode(false);
    }
  };

  const setRoundCloseMode = async (mode: HubEvent['roundCloseMode']) => {
    if (!ev) return;
    const prev = ev.roundCloseMode;
    setEv({ ...ev, roundCloseMode: mode });
    try {
      await api.put(`/api/events/${ev.id}`, { roundCloseMode: mode });
    } catch {
      setEv({ ...ev, roundCloseMode: prev });
      window.alert('Could not change the round setting');
    }
  };

  const [savingCheckin, setSavingCheckin] = useState(false);
  const setCheckIn = async (enabled: boolean) => {
    if (!ev) return;
    setSavingCheckin(true);
    setEv({ ...ev, checkInEnabled: enabled });
    try {
      await api.put(`/api/events/${ev.id}`, { checkInEnabled: enabled });
    } catch {
      setEv({ ...ev, checkInEnabled: !enabled });
      window.alert('Could not change check-in');
    } finally {
      setSavingCheckin(false);
    }
  };

  const checks = useMemo(() => (ev ? computeChecks(ev) : []), [ev]);
  const issues = checks.filter((c) => !c.ok).length;

  if (loading) {return <div className="gh-state">Loading game night…</div>;}
  if (error || !ev) {return <div className="gh-state gh-state-error">{error || 'Not found'}</div>;}

  const rounds = ev.quiz.rounds;
  const totalQuestions = rounds.reduce((n, r) => n + r.questions.length, 0);
  const audioRounds = rounds.filter((r) => r.roundType === 'AUDIO').length;
  const visualRounds = rounds.filter((r) => r.roundType === 'VISUAL').length;
  const activeTeams = ev.teams.filter((t) => !t.leftEarly);
  const people = activeTeams.reduce((n, t) => n + (t.playerCount || 0), 0);
  const displayName = ev.name?.trim() || ev.quiz.name;
  const isComplete = ev.status === 'COMPLETE';

  const presentUrl = `${window.location.host}/present/${ev.pin}`;

  const copyPresentUrl = () => {
    navigator.clipboard?.writeText(`${window.location.origin}/present/${ev.pin}`).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      },
      () => {},
    );
  };

  const handleAddTeam = async () => {
    const name = window.prompt('Team name?')?.trim();
    if (!name) return;
    const countStr = window.prompt('How many players?', '4');
    const playerCount = countStr ? parseInt(countStr, 10) || 1 : 1;
    setAddingTeam(true);
    try {
      await api.post(`/api/teams/event/${ev.id}`, { name, playerCount });
      await load();
    } catch {
      window.alert('Failed to add team');
    } finally {
      setAddingTeam(false);
    }
  };

  const markComplete = async () => {
    if (!window.confirm('Mark this game night complete?')) return;
    setMarking(true);
    try {
      await api.put(`/api/events/${ev.id}/status`, { status: 'COMPLETE' });
      await load();
    } catch {
      window.alert('Failed to mark complete');
    } finally {
      setMarking(false);
    }
  };

  const dateLine = new Date(ev.date).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

  // Pipeline: build ✓ / schedule ✓ are always done once a live event
  // exists. Preflight reflects issues; host&score is current unless
  // complete; results done when complete.
  const preflightState: StepState = issues > 0 ? 'warn' : 'done';
  const hostState: StepState = isComplete ? 'done' : 'current';
  const resultsState: StepState = isComplete ? 'current' : 'todo';

  return (
    <div className="game-hub">
      {/* Header */}
      <div className="gh-header">
        <Link to="/live-events" className="gh-back">&larr; Game Nights</Link>
        <div className="gh-header-title">
          <div className="gh-title">
            {displayName} <span className="gh-title-venue">at {ev.venue.name}</span>
          </div>
          <div className="gh-title-date">{dateLine}</div>
        </div>
        <span className={`gh-status-pill ${isComplete ? 'complete' : 'live'}`}>
          {isComplete ? 'COMPLETE' : 'LIVE'}
        </span>
        <button
          type="button"
          className="gh-btn gh-btn-primary"
          onClick={() => navigate(`/events/${ev.id}/host`)}
        >
          Open host panel
        </button>
      </div>

      {/* Pipeline stepper */}
      <div className="gh-pipeline">
        <Step state="done" label="Build quiz" num={1} />
        <Connector done />
        <Step state="done" label="Schedule" num={2} />
        <Connector done={preflightState === 'done'} />
        <Step
          state={preflightState}
          label={issues > 0 ? `Preflight · ${issues} issue${issues > 1 ? 's' : ''}` : 'Preflight'}
          num={3}
        />
        <Connector done={false} />
        <Step state={hostState} label="Host & score" num={4} />
        <Connector done={isComplete} />
        <Step state={resultsState} label="Results" num={5} />
      </div>

      {/* Card grid */}
      <div className="gh-grid">
        {/* QUIZ */}
        <div className="gh-card">
          <span className="gh-overline">QUIZ</span>
          <span className="gh-card-title">{ev.quiz.name}</span>
          <span className="gh-card-body">
            {rounds.length} rounds · {totalQuestions} questions
            {audioRounds > 0 && ` · ${audioRounds} audio round${audioRounds > 1 ? 's' : ''}`}
            {visualRounds > 0 && ` · ${visualRounds} visual round${visualRounds > 1 ? 's' : ''}`}
            <br />Est. run time {estimateRuntime(totalQuestions)}
          </span>
          <div className="gh-card-actions">
            <Link className="gh-pill-action" to={`/quizzes/${ev.quiz.id}/edit`}>Edit quiz</Link>
            <a
              className="gh-pill-action"
              href={`/quizzes/${ev.quiz.id}/preview`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Preview
            </a>
          </div>
        </div>

        {/* SCREENS & PINS */}
        <div className="gh-card">
          <span className="gh-overline">SCREENS &amp; PINS</span>
          <div className="gh-pin-row">
            <div className="gh-pin-tile">
              <div className="gh-pin-label">AUDIENCE PIN</div>
              <div className="gh-pin gh-pin-accent">{ev.pin}</div>
            </div>
            <div className="gh-pin-tile">
              <div className="gh-pin-label">ADMIN PIN</div>
              <div className="gh-pin">{ev.adminPin}</div>
            </div>
          </div>
          <div className="gh-url-row">
            <span className="gh-url">{presentUrl}</span>
            <button type="button" className="gh-url-copy" onClick={copyPresentUrl}>
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <div className="gh-card-actions">
            <a
              className="gh-pill-action"
              href={`/present/${ev.pin}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open projector view
            </a>
            <Link className="gh-pill-action" to={`/events/${ev.id}/score`}>Open scoring</Link>
          </div>
        </div>

        {/* TV SCOREBOARD */}
        <ScoreboardCard
          eventId={ev.id}
          pin={ev.pin}
          available={ev.features?.tvScoreboard !== false}
          settings={{
            scoreboardEnabled: ev.scoreboardEnabled,
            scoreboardTopN: ev.scoreboardTopN,
          }}
          onSaved={(next: ScoreboardSettings) => setEv((prev) => (prev ? { ...prev, ...next } : prev))}
        />

        {/* PREFLIGHT */}
        <div className={`gh-card ${issues > 0 ? 'gh-card-warn' : ''}`}>
          <span className={`gh-overline ${issues > 0 ? 'gh-overline-warn' : ''}`}>
            PREFLIGHT{issues > 0 ? ` · ${issues} ISSUE${issues > 1 ? 'S' : ''}` : ' · ALL CLEAR'}
          </span>
          {checks.map((c) => (
            <div className="gh-check" key={c.key}>
              <span className={`gh-check-icon ${c.ok ? 'ok' : 'warn'}`}>
                {c.ok ? '✓' : '!'}
              </span>
              <span className={`gh-check-label ${c.ok ? '' : 'warn'}`}>{c.label}</span>
              {c.detail && !c.ok && c.fixTo ? (
                <Link to={c.fixTo} className="gh-check-detail warn">{c.detail} · Fix</Link>
              ) : c.detail ? (
                <span className={`gh-check-detail ${c.ok ? '' : 'warn'}`}>{c.detail}</span>
              ) : null}
              {!c.ok && c.items && c.items.length > 0 && (
                <span className="gh-check-detail warn">
                  {c.items.map((it, i) => (
                    <span key={it.to}>
                      {i > 0 ? ', ' : ''}
                      <Link to={it.to} title={it.title}>{it.label}</Link>
                    </span>
                  ))}
                  {' · Fix'}
                </span>
              )}
            </div>
          ))}
          <div className="gh-card-note">Checks re-run automatically as the quiz changes.</div>
        </div>

        {/* TONIGHT'S OVERRIDES */}
        <div className="gh-card">
          <span className="gh-overline">TONIGHT&rsquo;S OVERRIDES</span>
          <span className="gh-card-body">
            Blank fields inherit the venue default. Override to change just this night.
          </span>
          <OverrideRow
            label="Display name"
            value={ev.name?.trim() || ev.quiz.name}
            source={ev.name?.trim() ? 'OVERRIDE' : 'typed on this night'}
          />
          <OverrideRow
            label="Start time"
            value={ev.welcome.startTime || '—'}
            source={overrideSource(ev.startTimeOverride, ev.venue.defaultStartTime, ev.quiz.welcomeStartTime)}
          />
          <OverrideRow
            label="Prizes"
            value={firstLine(ev.welcome.prizes) || '—'}
            source={overrideSource(ev.prizesOverride, ev.venue.defaultPrizes, ev.quiz.welcomePrizes)}
          />
          <OverrideRow
            label="Rules"
            value={firstLine(ev.welcome.rulesText) || '—'}
            source={overrideSource(ev.rulesTextOverride, ev.venue.defaultRulesText, ev.quiz.rulesText)}
            last
          />
          <Link to="/live-events" className="gh-pill-action gh-pill-full">Edit overrides</Link>
        </div>

        {/* SLIDES — custom slides placed into this night */}
        <NightSlidesCard
          liveEventId={ev.id}
          venueName={ev.venue.name}
          rounds={rounds}
          customSlides={ev.customSlides ?? []}
          onChange={load}
        />

        {/* TEAMS */}
        <div className="gh-card">
          <span className="gh-overline">
            TEAMS · {activeTeams.length} CHECKED IN
          </span>
          {activeTeams.length === 0 ? (
            <span className="gh-card-body">No teams checked in yet.</span>
          ) : (
            <div className="gh-team-chips">
              {activeTeams.slice(0, 4).map((t) => (
                <span className="gh-team-chip" key={t.id}>
                  {t.name} · {t.playerCount}
                </span>
              ))}
              {activeTeams.length > 4 && (
                <span className="gh-team-chip gh-team-chip-more">
                  + {activeTeams.length - 4} more
                </span>
              )}
            </div>
          )}
          <div className="gh-stats">
            <span className="gh-stat"><b>{people}</b> PEOPLE</span>
            <span className="gh-stat"><b>{activeTeams.length}</b> TEAMS</span>
          </div>
          <button
            type="button"
            className="gh-pill-action gh-pill-full"
            onClick={handleAddTeam}
            disabled={addingTeam}
          >
            {addingTeam ? 'Adding…' : '+ Add team'}
          </button>
        </div>

        {/* QR CHECK-IN */}
        <div className="gh-card">
          <span className="gh-overline">PHONE CHECK-IN</span>
          <label className="gh-checkin-toggle">
            <Switch
              checked={ev.checkInEnabled}
              disabled={savingCheckin || isComplete}
              onCheckedChange={(v) => setCheckIn(Boolean(v))}
            />
            <span>{ev.checkInEnabled ? 'On: teams can check in with the QR code' : 'Off: paper only'}</span>
          </label>
          <div className="gh-answer-mode">
            <span className="gh-card-body">Answers tonight</span>
            <SegmentedControl.Root
              value={ev.answerMode}
              onValueChange={(v) => setAnswerMode(v as HubEvent['answerMode'])}
              size="1"
              disabled={savingMode || isComplete}
            >
              <SegmentedControl.Item value="PAPER">Paper</SegmentedControl.Item>
              <SegmentedControl.Item value="PHONE">Phone</SegmentedControl.Item>
              <SegmentedControl.Item value="MIXED">Mixed</SegmentedControl.Item>
            </SegmentedControl.Root>
            {ev.answerMode !== 'PAPER' && (
              <Link className="gh-pill-action" to={`/events/${ev.id}/answers`}>Review phone answers</Link>
            )}
          </div>
          {ev.answerMode !== 'PAPER' && (
            <div className="gh-answer-mode">
              <span className="gh-card-body">Close a round for phones</span>
              <SegmentedControl.Root
                value={ev.roundCloseMode}
                onValueChange={(v) => setRoundCloseMode(v as HubEvent['roundCloseMode'])}
                size="1"
                disabled={isComplete}
              >
                <SegmentedControl.Item value="ON_REVEAL">When answers are revealed</SegmentedControl.Item>
                <SegmentedControl.Item value="MANUAL">Only by hand</SegmentedControl.Item>
              </SegmentedControl.Root>
              <span className="gh-card-note">
                Either way, you can open or close any round from the host panel or the review screen.
              </span>
            </div>
          )}
          {ev.checkInEnabled ? (
            <>
              <span className="gh-card-body">
                The projector shows this code on the welcome and rules slides.
              </span>
              <CheckInQr pin={ev.pin} size={140} />
              <CheckInPanel teams={ev.teams} onChanged={load} />
            </>
          ) : (
            <span className="gh-card-body">
              Turn on to let team captains check in from their phones. Paper scoring works the same either way.
            </span>
          )}
        </div>

        {/* AFTER THE NIGHT */}
        <div className={`gh-card ${isComplete ? '' : 'gh-card-dim'}`}>
          <span className="gh-overline">AFTER THE NIGHT</span>
          <span className="gh-card-body">
            Results, winner recap and attendance land here when the night is marked complete.
          </span>
          <div className="gh-card-actions">
            <Link className="gh-pill-action" to={`/events/${ev.id}/results`}>Results</Link>
            <button
              type="button"
              className="gh-pill-action"
              onClick={markComplete}
              disabled={marking || isComplete}
            >
              {isComplete ? 'Completed' : marking ? 'Marking…' : 'Mark complete'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function firstLine(s: string | null): string {
  if (!s) return '';
  return s.split('\n').map((l) => l.trim()).filter(Boolean)[0] || '';
}

function OverrideRow({
  label,
  value,
  source,
  last,
}: {
  label: string;
  value: string;
  source: 'OVERRIDE' | 'venue default' | 'typed on this night' | 'not set';
  last?: boolean;
}) {
  return (
    <div className={`gh-override-row ${last ? 'last' : ''}`}>
      <span className="gh-override-label">{label}</span>
      <span className="gh-override-value">
        {source === 'OVERRIDE' ? (
          <>
            {value} <span className="gh-override-badge">OVERRIDE</span>
          </>
        ) : (
          <span className="gh-override-source">{source}</span>
        )}
      </span>
    </div>
  );
}

type StepState = 'done' | 'warn' | 'current' | 'todo';

function Step({ state, label, num }: { state: StepState; label: string; num: number }) {
  return (
    <div className="gh-step">
      <span className={`gh-step-dot ${state}`}>
        {state === 'done' ? '✓' : state === 'warn' ? '!' : num}
      </span>
      <span className={`gh-step-label ${state}`}>{label}</span>
    </div>
  );
}

function Connector({ done }: { done: boolean }) {
  return <span className={`gh-connector ${done ? 'done' : ''}`} />;
}
